// The feature manifests — routes + breadcrumbs + sidebar entries + enablement, one block per
// feature. Apps compose dashboards from these (see manifest.tsx). Icons come from the icon SSOT;
// nav icons are CSS-sized (no explicit size), matching the sidebar's styling.
import {
  BarChartIcon,
  CheckIcon,
  ChipIcon,
  GridIcon,
  RackIcon,
  LayersIcon,
  MapperIcon,
  ScrollTextIcon,
  SlidersIcon,
  ShieldIcon,
  UsersIcon,
  WrenchIcon,
  AlertCircleIcon,
  AlertTriangleIcon,
  CloudIcon,
  LeafIcon,
  TruckIcon,
  BellIcon,
} from '@xantory/icons';
import { navigate, type FeatureManifest } from './manifest';
import { lazy } from 'preact/compat';
const MapperPage = lazy(() => import('./Mapper/mapper').then((m) => ({ default: m.MapperPage })));
const MicrocontrollersPage = lazy(() =>
  import('./Microcontrollers/microcontrollers').then((m) => ({ default: m.MicrocontrollersPage })),
);
const SensorsPage = lazy(() => import('./Sensors/sensors').then((m) => ({ default: m.SensorsPage })));
const ActuatorsPage = lazy(() => import('./Actuators/actuators').then((m) => ({ default: m.ActuatorsPage })));
const CalibrationPage = lazy(() =>
  import('./Calibration/calibration').then((m) => ({ default: m.CalibrationPage })),
);
const FirmwarePage = lazy(() => import('./Firmware/firmware').then((m) => ({ default: m.FirmwarePage })));
const FirmwarePushPage = lazy(() => import('./Firmware/push').then((m) => ({ default: m.FirmwarePushPage })));
const IoTControlPage = lazy(() =>
  import('./IoTControl/iot-control').then((m) => ({ default: m.IoTControlPage })),
);
const McHistoryPage = lazy(() =>
  import('./McHistory/mc-history').then((m) => ({ default: m.McHistoryPage })),
);
const DeviceHistoryPage = lazy(() =>
  import('./DeviceHistory/device-history').then((m) => ({ default: m.DeviceHistoryPage })),
);
const RegistrationPage = lazy(() =>
  import('./Registration/registration').then((m) => ({ default: m.RegistrationPage })),
);
const UserManagementPage = lazy(() =>
  import('./UserManagement/user-management').then((m) => ({ default: m.UserManagementPage })),
);
const AccessRolesPage = lazy(() =>
  import('./AccessRoles/access-roles').then((m) => ({ default: m.AccessRolesPage })),
);
const SitesPage = lazy(() => import('./Sites/sites').then((m) => ({ default: m.SitesPage })));
const AuditLogPage = lazy(() => import('./AuditLog/audit-log').then((m) => ({ default: m.AuditLogPage })));
const NotificationsPage = lazy(() =>
  import('./Notifications/notifications').then((m) => ({ default: m.NotificationsPage })),
);
const ItsmCommonsPage = lazy(() =>
  import('./ItsmCommons/itsm-commons').then((m) => ({ default: m.ItsmCommonsPage })),
);
const MasterDataPage = lazy(() =>
  import('./MasterData/master-data').then((m) => ({ default: m.MasterDataPage })),
);
const CultivationPage = lazy(() =>
  import('./Cultivation/cultivation').then((m) => ({ default: m.CultivationPage })),
);
const CapacityPage = lazy(() =>
  import('./ProductionPlanning/capacity').then((m) => ({ default: m.CapacityPage })),
);
const PlansPage = lazy(() => import('./ProductionPlanning/plans').then((m) => ({ default: m.PlansPage })));
const PlanRecordPage = lazy(() =>
  import('./ProductionPlanning/plan-record').then((m) => ({ default: m.PlanRecordPage })),
);
const PlanningAlertsPage = lazy(() =>
  import('./ProductionPlanning/alerts').then((m) => ({ default: m.AlertsPage })),
);
const PlanningTimelinePage = lazy(() =>
  import('./ProductionPlanning/timeline').then((m) => ({ default: m.TimelinePage })),
);
const PlanningTasksPage = lazy(() =>
  import('./ProductionPlanning/tasks').then((m) => ({ default: m.TasksPage })),
);
const DeviceCatalogPage = lazy(() =>
  import('./DeviceCatalog/device-catalog').then((m) => ({ default: m.DeviceCatalogPage })),
);
const FleetInventoryPage = lazy(() =>
  import('./FleetInventory/fleet-inventory').then((m) => ({ default: m.FleetInventoryPage })),
);
const AssetLifecyclePage = lazy(() =>
  import('./AssetLifecycle/asset-lifecycle').then((m) => ({ default: m.AssetLifecyclePage })),
);
const WarehousePage = lazy(() => import('./Warehouse/warehouse').then((m) => ({ default: m.WarehousePage })));
// Procurement (R-234) — five screens, one per step of the buying chain.
const ProcurementRequestsPage = lazy(() =>
  import('./Procurement/requests').then((m) => ({ default: m.ProcurementRequestsPage })),
);
const ProcurementRequestRecordPage = lazy(() =>
  import('./Procurement/request-record').then((m) => ({ default: m.ProcurementRequestRecordPage })),
);
const ProcurementApprovalsPage = lazy(() =>
  import('./Procurement/approvals').then((m) => ({ default: m.ProcurementApprovalsPage })),
);
const ProcurementOrdersPage = lazy(() =>
  import('./Procurement/orders').then((m) => ({ default: m.ProcurementOrdersPage })),
);
const ProcurementRulesPage = lazy(() =>
  import('./Procurement/rules').then((m) => ({ default: m.ProcurementRulesPage })),
);
const AssetLabelsPage = lazy(() =>
  import('./AssetLifecycle/asset-labels').then((m) => ({ default: m.AssetLabelsPage })),
);
const AssetIntakePage = lazy(() =>
  import('./AssetLifecycle/asset-intake').then((m) => ({ default: m.AssetIntakePage })),
);
const AssetScanPage = lazy(() =>
  import('./AssetLifecycle/asset-scan').then((m) => ({ default: m.AssetScanPage })),
);
const AssetClassesPage = lazy(() =>
  import('./AssetLifecycle/asset-classes').then((m) => ({ default: m.AssetClassesPage })),
);
const AssetModelsPage = lazy(() =>
  import('./AssetLifecycle/asset-models').then((m) => ({ default: m.AssetModelsPage })),
);
const AssetLabelPoolPage = lazy(() =>
  import('./AssetLifecycle/asset-label-pool').then((m) => ({ default: m.AssetLabelPoolPage })),
);
const AssetUnregisteredPage = lazy(() =>
  import('./AssetLifecycle/asset-unregistered').then((m) => ({ default: m.AssetUnregisteredPage })),
);
const AssetTransfersPage = lazy(() =>
  import('./AssetLifecycle/asset-transfers').then((m) => ({ default: m.AssetTransfersPage })),
);
const TransferOrdersPage = lazy(() =>
  import('./AssetLifecycle/transfer-orders').then((m) => ({ default: m.TransferOrdersPage })),
);
const ReceivingPage = lazy(() =>
  import('./AssetLifecycle/receiving').then((m) => ({ default: m.ReceivingPage })),
);
const AssetRecordPage = lazy(() =>
  import('./AssetLifecycle/asset-record').then((m) => ({ default: m.AssetRecordPage })),
);
const LocationsPage = lazy(() => import('./Locations/locations').then((m) => ({ default: m.LocationsPage })));
const MaintenancePage = lazy(() =>
  import('./Maintenance/maintenance').then((m) => ({ default: m.MaintenancePage })),
);
// R-244: work orders became their own screen. Both live in Maintenance/ still —
// the folder name is internal and moving it would churn a dozen import paths for
// no behavioural gain.
const IncidentRecordPage = lazy(() =>
  import('./Maintenance/site-incidents').then((m) => ({ default: m.IncidentRecordPage })),
);
const ChangeRecordPage = lazy(() =>
  import('./Maintenance/site-changes').then((m) => ({ default: m.ChangeRecordPage })),
);
const WorkOrdersPage = lazy(() =>
  import('./Maintenance/work-orders').then((m) => ({ default: m.WorkOrdersPage })),
);
const WorkOrdersLegacyRedirect = lazy(() =>
  import('./Maintenance/work-orders').then((m) => ({ default: m.WorkOrdersLegacyRedirect })),
);
const ProblemsPage = lazy(() => import('./Problems/problems').then((m) => ({ default: m.ProblemsPage })));
const SocPage = lazy(() => import('./Soc/soc').then((m) => ({ default: m.SocPage })));
const LogisticsPage = lazy(() => import('./Logistics/logistics').then((m) => ({ default: m.LogisticsPage })));
const LogisticsOrderPage = lazy(() =>
  import('./Logistics/logistics').then((m) => ({ default: m.LogisticsOrderPage })),
);
const LogisticsTourPage = lazy(() =>
  import('./Logistics/logistics').then((m) => ({ default: m.LogisticsTourPage })),
);
const SalesPage = lazy(() => import('./Sales/sales').then((m) => ({ default: m.SalesPage })));
const CockpitPage = lazy(() => import('./Cockpit/cockpit').then((m) => ({ default: m.CockpitPage })));
const OverviewPage = lazy(() => import('./Overview/overview').then((m) => ({ default: m.OverviewPage })));
const SettingsPage = lazy(() => import('./Settings/settings').then((m) => ({ default: m.SettingsPage })));
const CompleteRegistrationPage = lazy(() =>
  import('./CompleteRegistration/complete-registration').then((m) => ({
    default: m.CompleteRegistrationPage,
  })),
);
const RecoverPage = lazy(() => import('./Recover/recover').then((m) => ({ default: m.RecoverPage })));
const MobileHomePage = lazy(() =>
  import('./TechnicianMobile/mobile-home').then((m) => ({ default: m.MobileHomePage })),
);
const MyWorkPage = lazy(() => import('./TechnicianMobile/my-work').then((m) => ({ default: m.MyWorkPage })));
const TaskDetailPage = lazy(() =>
  import('./TechnicianMobile/task-detail').then((m) => ({ default: m.TaskDetailPage })),
);
const MobileScanPage = lazy(() =>
  import('./TechnicianMobile/mobile-scan').then((m) => ({ default: m.MobileScanPage })),
);
const MobileProfilePage = lazy(() =>
  import('./TechnicianMobile/mobile-profile').then((m) => ({ default: m.MobileProfilePage })),
);
const MfaRecoverPage = lazy(() => import('./Login/mfa-recover').then((m) => ({ default: m.MfaRecoverPage })));

/** Fleet overview (R-085) — the cloud landing page; the HQ cross-site view (D-058). */
import { ROUTES, href } from './routes';

export const overviewFeature: FeatureManifest = {
  key: 'overview',
  enabled: ({ mode }) => mode === 'cloud',
  routes: [{ path: ROUTES.overview, breadcrumb: ['HQ', 'Fleet Overview'], render: () => <OverviewPage /> }],
  nav: {
    section: 'HQ',
    item: {
      kind: 'leaf',
      key: ROUTES.overview,
      label: 'Fleet Overview',
      icon: <CloudIcon />,
      href: ROUTES.overview,
    },
  },
};

/** Fleet Inventory (R-097 P3) — cloud HQ read of every site's auto-discovered
 *  devices; gated by the `devices` module (mirrors the site-local catalog read). */
export const fleetInventoryFeature: FeatureManifest = {
  key: 'fleet-inventory',
  enabled: ({ mode, can }) => mode === 'cloud' && can('devices', 'read'),
  routes: [
    {
      path: ROUTES.fleetInventory,
      breadcrumb: ['HQ', 'Fleet Inventory'],
      render: () => <FleetInventoryPage />,
    },
  ],
  nav: {
    section: 'HQ',
    item: {
      kind: 'leaf',
      key: ROUTES.fleetInventory,
      label: 'Fleet Inventory',
      icon: <GridIcon />,
      href: ROUTES.fleetInventory,
    },
  },
};

export const mapperFeature: FeatureManifest = {
  key: 'mapper',
  // Site setup — available on both tiers; gated by the `mapper` module (R-088).
  enabled: ({ can }) => can('mapper', 'read'),
  routes: [{ path: ROUTES.mapper, breadcrumb: ['Setup', 'Mapper'], render: () => <MapperPage /> }],
  nav: {
    section: 'Setup',
    item: { kind: 'leaf', key: ROUTES.mapper, label: 'Mapper', icon: <MapperIcon />, href: ROUTES.mapper },
  },
};

export const devicesFeature: FeatureManifest = {
  key: 'devices',
  // Both tiers; `devices:read` for the readings themselves (R-088) — but EVERY
  // page here first enumerates the iot-manager TOPOLOGY (rooms/racks/nodes, and
  // for Microcontrollers the controllers) to know what devices exist, and those
  // endpoints are gated server-side on `mapper:read`. A role holding `devices`
  // but not `mapper` (D-061 withholds it from `viewer`) therefore saw the group
  // and got a 403 on every page load. Gate on what the pages actually need.
  enabled: ({ can }) => can('devices', 'read') && can('mapper', 'read'),
  routes: [
    {
      path: ROUTES.devicesMicrocontrollers,
      breadcrumb: ['IoT Devices', 'Microcontrollers'],
      render: () => <MicrocontrollersPage />,
    },
    { path: ROUTES.devicesSensors, breadcrumb: ['IoT Devices', 'Sensors'], render: () => <SensorsPage /> },
    {
      path: ROUTES.devicesActuators,
      breadcrumb: ['IoT Devices', 'Actuators'],
      render: () => <ActuatorsPage />,
    },
    // R-227. Sits with the device screens because it is about the same boards,
    // but every mutation on it is admin-gated server-side.
    { path: ROUTES.firmware, breadcrumb: ['IoT Devices', 'Firmware'], render: () => <FirmwarePage /> },
    {
      path: ROUTES.firmwarePush,
      breadcrumb: ['Firmware', 'Push'],
      render: (q: URLSearchParams) => <FirmwarePushPage artifactId={q.get('artifact') ?? ''} />,
    },
  ],
  nav: {
    section: 'Setup',
    item: {
      kind: 'group',
      key: 'farm-iot',
      label: 'IoT Devices',
      icon: <ChipIcon />,
      children: [
        {
          key: ROUTES.devicesMicrocontrollers,
          label: 'Microcontrollers',
          href: ROUTES.devicesMicrocontrollers,
        },
        { key: ROUTES.devicesSensors, label: 'Sensors', href: ROUTES.devicesSensors },
        { key: ROUTES.devicesActuators, label: 'Actuators', href: ROUTES.devicesActuators },
        { key: ROUTES.firmware, label: 'Firmware', href: ROUTES.firmware },
      ],
    },
  },
};

/** Sensor calibration (backend R-225/D-136, R-332) — its own feature, NOT a route
 *  on `devicesFeature`, and the reason is the permission set.
 *
 *  `GET /v1/devices/calibrations/probes` is gated server-side on **`farming:read`
 *  AND `mapper:read`**: it reads the calibration register (farming) and joins the
 *  device topology and inventory to it (mapper). `devicesFeature` gates on
 *  `devices:read && mapper:read`, so hanging this route there would show the leaf
 *  to a role holding devices+mapper but not farming, and every page load would
 *  403 — precisely the bug `devicesFeature`'s own comment records having hit once.
 *
 *  A separate manifest is the supported shape: nav groups MERGE by `key`, and each
 *  child stays gated by its own feature. So the leaf appears inside "IoT Devices"
 *  for roles that can actually use it, and simply is not there for the rest —
 *  route and nav dropping out together, which is the registry's guarantee. */
export const calibrationFeature: FeatureManifest = {
  key: 'calibration',
  enabled: ({ can }) => can('farming', 'read') && can('mapper', 'read') && can('devices', 'read'),
  routes: [
    {
      path: ROUTES.devicesCalibration,
      breadcrumb: ['IoT Devices', 'Calibration'],
      render: () => <CalibrationPage />,
    },
  ],
  nav: {
    section: 'Setup',
    item: {
      kind: 'group',
      key: 'farm-iot',
      label: 'IoT Devices',
      icon: <ChipIcon />,
      children: [{ key: ROUTES.devicesCalibration, label: 'Calibration', href: ROUTES.devicesCalibration }],
    },
  },
};

/** Live farm operation — the "Farm" group of the Sentinel_AppV2 prototype (its Farm Dashboard ≈ Overview). */
export const iotControlFeature: FeatureManifest = {
  key: 'iot-control',
  // Both tiers. The page is built ENTIRELY from the iot-manager topology
  // (rooms/racks/nodes/microcontrollers), and every one of those endpoints is
  // gated server-side on `mapper:read` — so `devices:read` alone let a role
  // WITHOUT mapper (D-061 withholds it from `viewer`) open a page whose every
  // request then 403'd. Gate on what the data actually needs: both, or the
  // page is dead on arrival. Toggling actuators stays a finer in-page
  // `actuator-control:write` gate (R-088).
  enabled: ({ can }) => can('devices', 'read') && can('mapper', 'read'),
  // R-102: one page — a left room panel (in content) + a detail area that swaps room↔section in
  // place (no route hopping). Legacy scope paths fold to /farm/control in Router.normalize.
  routes: [
    {
      path: ROUTES.iotControl,
      breadcrumb: ['Farm', 'IoT Control'],
      render: () => <IoTControlPage />,
    },
  ],
  nav: {
    section: 'Farm',
    item: {
      kind: 'leaf',
      key: ROUTES.iotControl,
      label: 'IoT Control',
      icon: <SlidersIcon />,
      href: ROUTES.iotControl,
    },
  },
};

/** Cockpit / analytics (R-069/D-071) — Monitor section, RBAC `maintenance` module. */
export const cockpitFeature: FeatureManifest = {
  key: 'cockpit',
  enabled: ({ mode, can }) => mode === 'cloud' && can('maintenance', 'read'),
  routes: [{ path: ROUTES.cockpit, breadcrumb: ['Monitor', 'Cockpit'], render: () => <CockpitPage /> }],
  nav: {
    section: 'Monitor',
    item: {
      kind: 'leaf',
      key: ROUTES.cockpit,
      label: 'Cockpit',
      icon: <BarChartIcon />,
      href: ROUTES.cockpit,
    },
  },
};

/** SOC console (R-068/D-070) — Monitor section, RBAC `maintenance` module. */
export const socFeature: FeatureManifest = {
  key: 'soc',
  enabled: ({ mode, can }) => mode === 'cloud' && can('maintenance', 'read'),
  routes: [
    {
      path: ROUTES.soc,
      breadcrumb: ['Monitor', 'SOC'],
      // R-173: `?alarm=<id>` opens that alarm's detail directly — the destination
      // a notification about an alarm resolves to.
      render: (q) => <SocPage alarmId={q.get('alarm') ?? undefined} />,
    },
  ],
  nav: {
    section: 'Monitor',
    item: { kind: 'leaf', key: ROUTES.soc, label: 'SOC', icon: <AlertTriangleIcon />, href: ROUTES.soc },
  },
};

/** Asset Lifecycle / CMDB (R-063/D-065) — cloud console, RBAC `assets` module. */
export const assetsFeature: FeatureManifest = {
  key: 'assets',
  // R-122: operational on the LOCAL stack, readable from cloud via the /sites proxy.
  enabled: ({ can }) => can('assets', 'read'),
  routes: [
    {
      path: ROUTES.assets,
      breadcrumb: ['ITSM', 'Asset Register'],
      render: (q) => <AssetLifecyclePage initialState={q.get('state')} initialClass={q.get('class')} />,
    },
    // R-144: transfer orders — the movement document + reservation ledger.
    {
      // R-235: this path now serves the movement LEDGER. The transfer-ORDER
      // document moved to Maintenance — see the route comment.
      path: ROUTES.assetsTransfers,
      breadcrumb: ['ITSM', 'Transfers'],
      render: () => <AssetTransfersPage />,
    },
    {
      path: ROUTES.assetsRecord,
      breadcrumb: ['Asset Lifecycle', 'Asset record'],
      render: (q) => <AssetRecordPage id={q.get('id') ?? ''} />,
    },
    // R-129: printable QR labels (?tags=a,b,c) + the scan-to-complete flow.
    {
      // R-235: the POOL browser (class → batch → sheet).
      path: ROUTES.assetsLabels,
      breadcrumb: ['ITSM', 'QR Labels'],
      render: (q) => <AssetLabelPoolPage classCode={q.get('class')} batchId={q.get('batch')} />,
    },
    {
      path: ROUTES.assetsLabelsPrint,
      breadcrumb: ['ITSM', 'QR Labels — print'],
      render: (q) => <AssetLabelsPage tags={q.get('tags') ?? ''} batchId={q.get('batch')} />,
    },
    // R-235: the manual birth path. Its own page rather than a modal — it owns a
    // scanner viewfinder and two validation steps before any form appears.
    {
      path: ROUTES.assetsIntake,
      breadcrumb: ['ITSM', 'Register / Intake'],
      render: (q) => <AssetIntakePage device={q.get('device') ?? undefined} />,
    },
    // R-235: the transfer-ORDER document, relocated out of the Assets group so it
    // stops competing with the ledger for the word "Transfers".
    {
      path: ROUTES.maintenanceTransferOrders,
      breadcrumb: ['ITSM', 'Transfer orders'],
      render: () => <TransferOrdersPage />,
    },
    // R-235: the taxonomy and the catalogue, on the path the setup chain follows
    // (classes → models → labels → intake) rather than buried in Admin.
    {
      path: ROUTES.assetsClasses,
      breadcrumb: ['ITSM', 'Asset Classes'],
      render: (q) => <AssetClassesPage initialSearch={q.get('q')} />,
    },
    {
      path: ROUTES.assetsModels,
      breadcrumb: ['ITSM', 'Models (SKUs)'],
      render: (q) => <AssetModelsPage initialClass={q.get('class')} openNew={q.get('new') === '1'} />,
    },
    {
      path: ROUTES.assetsUnregistered,
      breadcrumb: ['ITSM', 'Unregistered'],
      render: () => <AssetUnregisteredPage />,
    },
    { path: ROUTES.assetsScan, breadcrumb: ['Asset Lifecycle', 'Scan'], render: () => <AssetScanPage /> },
  ],
  // Assets group. Register is the full CMDB across all lifecycle states; the
  // stockroom is merged in as its "In stock" filter (R-147), not a separate
  // entry — filtering Register to In stock reveals the substatus chips + pick order.
  nav: {
    section: 'ITSM',
    item: {
      kind: 'group',
      key: 'itsm-assets',
      label: 'Assets',
      icon: <GridIcon />,
      children: [
        // R-235: the prototype's own `VIEWS` titles, verbatim (V8:2638). The
        // earlier label was a bare 'All' — chosen when the only sibling was
        // Scan/Transfers and 'Register' would have read as the VERB. That reasoning
        // expired the moment 'Register / Intake' became a sibling: 'All' under a
        // list that now contains a Register entry says less, not more.
        { key: ROUTES.assets, label: 'All Assets', href: ROUTES.assets },
        // R-235: the prototype's creation order is classes → models → labels →
        // intake, and Register sits right after All because it is the verb the
        // register's own list sends you to.
        { key: ROUTES.assetsIntake, label: 'Register / Intake', href: ROUTES.assetsIntake },
        { key: ROUTES.assetsClasses, label: 'Asset Classes', href: ROUTES.assetsClasses },
        { key: ROUTES.assetsModels, label: 'Models (SKUs)', href: ROUTES.assetsModels },
        { key: ROUTES.assetsTransfers, label: 'Transfers', href: ROUTES.assetsTransfers },
        { key: ROUTES.assetsScan, label: 'Scan', href: ROUTES.assetsScan },
        { key: ROUTES.assetsLabels, label: 'QR Labels', href: ROUTES.assetsLabels },
        { key: ROUTES.assetsUnregistered, label: 'Unregistered', href: ROUTES.assetsUnregistered },
      ],
    },
  },
};

/** Site Receiving (R-145, reshaped by R-234 P4) — goods receipt against an open
 * PO. THIS is the step that creates assets; nothing before it mints a record.
 *
 * ⚠ RBAC moved `warehouse` → `receiving` in R-234 P0: the storekeeper who books
 * goods in is a different seat from the buyer who ordered them, and one
 * `warehouse` module could not express that. Gating on the old module here would
 * show the screen to the wrong people and hide it from the right ones.
 *
 * Reachable from BOTH tiers, like the rest of procurement: the routes live on
 * gateway-local and the cloud console gets there through the R-154 `/sites/{id}`
 * drill-down, with RBAC enforced by the gateway either way — the tier was never
 * the thing keeping people out, `receiving` is.
 *
 * ⚠ It was held back to `local` until the offline queue pinned its destination.
 * A queued receipt stored a LOGICAL path and resolved it on replay, so switching
 * farms between queueing and reconnecting booked the goods into the wrong one.
 * `offline-queue.ts` now freezes the site at enqueue; without that, do not widen
 * a queueable write to cloud mode.
 *
 * Sits 4th in the Procurement section — the prototype's day-to-day order — which
 * is why it is declared between Purchase Orders and Approval Rules in FEATURES.
 */
export const receivingFeature: FeatureManifest = {
  key: 'receiving',
  enabled: ({ can }) => can('receiving', 'read'),
  routes: [
    {
      path: ROUTES.procurementReceiving,
      breadcrumb: ['Procurement', 'Receiving'],
      render: () => <ReceivingPage />,
    },
  ],
  nav: {
    section: 'Procurement',
    item: {
      kind: 'leaf',
      key: ROUTES.procurementReceiving,
      label: 'Receiving',
      icon: <TruckIcon />,
      href: ROUTES.procurementReceiving,
    },
  },
};

/**
 * Incidents (R-067/D-069, split out by R-248) — ITSM section, RBAC `incidents`.
 *
 * ⚠⚠ TWO FEATURES, ONE NAV GROUP, and the split is not cosmetic. Reporting a fault
 * and planning a change are different authorities: the prototype's ACL puts seven
 * seats on `inc.create` ("anyone who uses equipment may say it is broken") and only
 * the maintenance seats on the change side. Under one `maintenance`-gated feature a
 * buyer with `incidents: write` would have seen **no Maintenance group at all** and
 * had no way to reach the screen they are explicitly allowed to use.
 *
 * `buildRegistry` merges nav groups that share a `key` and keeps every child gated by
 * its own feature's `enabled`, so both halves render under one "Maintenance" group and
 * a seat sees only the halves it holds. Registered BEFORE the change half so Incidents
 * reads above Changes.
 */
export const incidentsFeature: FeatureManifest = {
  key: 'incidents',
  // R-125: incidents operate on the LOCAL stack; the legacy cloud console
  // serves cloud mode until the R-126 cutover.
  enabled: ({ can }) => can('incidents', 'read'),
  routes: [
    {
      path: ROUTES.maintenanceIncidents,
      breadcrumb: ['Maintenance', 'Incidents'],
      // R-173: `?id=` opens that incident's row expanded (notification deep link).
      render: (q) => <MaintenancePage tab="incidents" query={q} />,
    },
    {
      // R-244 — one incident, as its own page. `?id=` rather than a path segment
      // to match the two record pages that already exist (assets, procurement).
      path: ROUTES.maintenanceIncidentRecord,
      breadcrumb: ['Maintenance', 'Incident'],
      render: (q) => <IncidentRecordPage id={q.get('id') ?? ''} />,
    },
  ],
  nav: {
    section: 'ITSM',
    item: {
      kind: 'group',
      key: 'itsm-maintenance',
      label: 'Maintenance',
      icon: <WrenchIcon />,
      children: [{ key: ROUTES.maintenanceIncidents, label: 'Incidents', href: ROUTES.maintenanceIncidents }],
    },
  },
};

/** Changes (R-067/D-069) — ITSM section, RBAC `maintenance` module. See the note on
 *  `incidentsFeature` for why these are two features sharing one nav group. */
export const maintenanceFeature: FeatureManifest = {
  key: 'maintenance',
  enabled: ({ can }) => can('maintenance', 'read'),
  routes: [
    {
      path: ROUTES.maintenanceChanges,
      breadcrumb: ['Maintenance', 'Changes'],
      // L-8/K-8 deep-links: ?create=1&incident_id=… prefills the create modal;
      // ?id=… opens a change detail.
      render: (q) => <MaintenancePage tab="changes" query={q} />,
    },
    {
      path: ROUTES.maintenanceChangeRecord,
      breadcrumb: ['Maintenance', 'Change'],
      render: (q) => <ChangeRecordPage id={q.get('id') ?? ''} />,
    },
    {
      // R-244: the pre-split path, kept ONLY so an existing bookmark or
      // notification deep link still lands somewhere real. It forwards to
      // ROUTES.workOrders and nothing new should point here.
      path: ROUTES.maintenanceWorkOrders,
      breadcrumb: ['Maintenance', 'Work orders'],
      render: () => <WorkOrdersLegacyRedirect />,
    },
  ],
  nav: {
    section: 'ITSM',
    item: {
      kind: 'group',
      key: 'itsm-maintenance',
      label: 'Maintenance',
      icon: <WrenchIcon />,
      children: [{ key: ROUTES.maintenanceChanges, label: 'Changes', href: ROUTES.maintenanceChanges }],
    },
  },
};

/** Work orders (R-128, re-homed by R-244) — preventive + corrective maintenance.
 *  Its own screen rather than a third Maintenance tab: the V8 prototype's Service
 *  section is Incidents + Changes and has no work-order view at all, and PM is a
 *  different question from restoring service. Same RBAC module (`maintenance`) and
 *  same ITSM section, so nothing about who can reach it changed — only where it
 *  sits. Registered AFTER maintenanceFeature so it reads below Maintenance in the
 *  sidebar (features sharing a section group in manifest-list order). */
export const workOrdersFeature: FeatureManifest = {
  key: 'work-orders',
  enabled: ({ can }) => can('maintenance', 'read'),
  routes: [
    {
      path: ROUTES.workOrders,
      breadcrumb: ['ITSM', 'Work orders'],
      render: () => <WorkOrdersPage />,
    },
  ],
  nav: {
    section: 'ITSM',
    item: {
      kind: 'leaf',
      key: ROUTES.workOrders,
      label: 'Work orders',
      icon: <WrenchIcon />,
      href: ROUTES.workOrders,
    },
  },
};

/** Problem / Known-Error + Knowledge-base lifecycle (R-143) — cloud tier only
 * (the masters replicate down to every site, D6); `maintenance` module. Each
 * process is its own nav leaf (workshop ServiceNow-module shape). */
export const problemsFeature: FeatureManifest = {
  key: 'problems',
  enabled: ({ mode, can }) => mode === 'cloud' && can('maintenance', 'read'),
  routes: [
    {
      path: ROUTES.problems,
      breadcrumb: ['ITSM', 'Problems'],
      render: () => <ProblemsPage tab="problems" />,
    },
    {
      path: ROUTES.knowledgeBase,
      breadcrumb: ['ITSM', 'Knowledge Base'],
      render: () => <ProblemsPage tab="kb" />,
    },
  ],
  nav: {
    section: 'ITSM',
    item: {
      kind: 'group',
      key: 'itsm-problems',
      label: 'Problems & KB',
      icon: <ScrollTextIcon />,
      children: [
        { key: ROUTES.problems, label: 'Problems', href: ROUTES.problems },
        { key: ROUTES.knowledgeBase, label: 'Knowledge Base', href: ROUTES.knowledgeBase },
      ],
    },
  },
};

/** Warehouse (R-064/D-068) — Commerce section, RBAC `warehouse` module. */
export const warehouseFeature: FeatureManifest = {
  key: 'warehouse',
  enabled: ({ mode, can }) => mode === 'cloud' && can('warehouse', 'read'),
  routes: [
    { path: ROUTES.warehouse, breadcrumb: ['Commerce', 'Warehouse'], render: () => <WarehousePage /> },
  ],
  nav: {
    section: 'Commerce',
    item: {
      kind: 'leaf',
      key: ROUTES.warehouse,
      label: 'Warehouse',
      icon: <RackIcon />,
      href: ROUTES.warehouse,
    },
  },
};

/** Procurement (R-234) — the site-local buying chain, rewritten to the team's
 * prototype: request → approve → order → receive.
 *
 * ⚠ ITS OWN SECTION, not a group under Commerce. The prototype is explicit that
 * procurement is a module in its own right — it touches ITSM at exactly two
 * seams (it can order nothing but a catalogue model, and receiving is what turns
 * an order into assets) and is otherwise a separate chain. A section also gives
 * the leaves their exact prototype order, which a shared group could not: nav
 * children merge append-only, so two features feeding one group can never
 * interleave. One feature per leaf, ordered by the FEATURES array.
 *
 * ⚠ NOT cloud-only. The routes live on gateway-local so a farm can buy with no
 * uplink, and the cloud console reaches the SAME routes through the /sites/{id}
 * drill-down (`resolvePath`). One implementation, both tiers.
 *
 * ⚠ EACH LEAF GATES ON ITS OWN RBAC MODULE. The chain is three modules on
 * purpose — `procurement` (the request), `purchasing` (the order), `receiving`
 * (the goods in) — because no seat does two steps. Collapsing them into one
 * blanket check here would show a buyer the storekeeper's screen and hide the
 * order from the person who issues it.
 */
export const procurementRequestsFeature: FeatureManifest = {
  key: 'procurement-requests',
  enabled: ({ can }) => can('procurement', 'read'),
  routes: [
    {
      path: ROUTES.procurementRequests,
      breadcrumb: ['Procurement', 'Requests'],
      render: () => <ProcurementRequestsPage />,
    },
    {
      // Locked sub-page of Requests — `?id=`, because the router has no path params.
      path: ROUTES.procurementRequestRecord,
      breadcrumb: ['Procurement', 'Request'],
      render: (q) => <ProcurementRequestRecordPage id={q.get('id') ?? undefined} />,
    },
  ],
  nav: {
    section: 'Procurement',
    item: {
      kind: 'leaf',
      key: ROUTES.procurementRequests,
      label: 'Requests',
      icon: <ScrollTextIcon />,
      href: ROUTES.procurementRequests,
    },
  },
};

export const procurementApprovalsFeature: FeatureManifest = {
  key: 'procurement-approvals',
  // ⚠⚠ `approvals`, NOT `procurement` — the mirror of the backend's R-249. R-309
  // moved these two screens out of Procurement because approvals are not
  // procurement's; the PERMISSION stayed behind, and that was the whole bug: a
  // `chg_approver` holds NO `procurement` cell, so the seat whose entire job is
  // deciding got a 403 on its own queue AND no nav group to reach it from.
  enabled: ({ can }) => can('approvals', 'read'),
  routes: [
    {
      path: ROUTES.procurementApprovals,
      breadcrumb: ['Approvals', 'My Approvals'],
      render: () => <ProcurementApprovalsPage />,
    },
  ],
  // ⚠⚠ R-309 (USER-DIRECTED 2026-08-25) — APPROVALS ARE NOT PROCUREMENT'S.
  //
  // One engine and one rule table serve BOTH queues: `approval_rules.record_type`
  // is `procurement` | `change`. Filing these two screens under Procurement said
  // otherwise, and that is exactly what went wrong — every rule an operator wrote
  // was a purchasing rule, so a change request never matched one.
  //
  // ⚠ This is a deliberate DEPARTURE FROM THE PROTOTYPE, which keeps Approval Rules
  // under Procurement. The user called it and they are right: the prototype's
  // placement is what invited the mistake in the first place.
  //
  // Both screens now live in ONE group under Administration, so the queue and the
  // rules that feed it are read in the same place.
  nav: {
    section: 'Administration',
    item: {
      kind: 'group',
      key: 'approvals',
      label: 'Approvals',
      icon: <CheckIcon />,
      children: [
        { key: ROUTES.procurementApprovals, label: 'My Approvals', href: ROUTES.procurementApprovals },
        { key: ROUTES.procurementRules, label: 'Approval Rules', href: ROUTES.procurementRules },
      ],
    },
  },
};

/** `purchasing`, not `procurement`: the buyer's screen. */
export const procurementOrdersFeature: FeatureManifest = {
  key: 'procurement-orders',
  enabled: ({ can }) => can('purchasing', 'read'),
  routes: [
    {
      path: ROUTES.procurementOrders,
      breadcrumb: ['Procurement', 'Purchase Orders'],
      render: () => <ProcurementOrdersPage />,
    },
  ],
  nav: {
    section: 'Procurement',
    item: {
      kind: 'leaf',
      key: ROUTES.procurementOrders,
      label: 'Purchase Orders',
      icon: <BarChartIcon />,
      href: ROUTES.procurementOrders,
    },
  },
};

/** Set up ONCE and read rarely, so it sits last — but it has to EXIST first, or
 * no rule matches and every request is silently auto-approved. */
export const procurementRulesFeature: FeatureManifest = {
  key: 'procurement-rules',
  // `approvals: read` to SEE the rules (the backend gates the list the same way);
  // WRITING them is `approvals: admin`, checked inside the screen.
  enabled: ({ can }) => can('approvals', 'read'),
  routes: [
    {
      path: ROUTES.procurementRules,
      breadcrumb: ['Approvals', 'Approval Rules'],
      render: () => <ProcurementRulesPage />,
    },
  ],
  // ⚠ R-309 — NO nav entry of its own any more: the `approvals` GROUP above owns
  // both links. The route stays exactly where it was, because notifications, the
  // procurement empty-state and anybody's bookmark point at it — moving a screen
  // must not break a URL somebody already has.
};

/** Logistics (R-074/D-077) — Commerce section, RBAC `logistics` module. */
export const logisticsFeature: FeatureManifest = {
  key: 'logistics',
  enabled: ({ mode, can }) => mode === 'cloud' && can('logistics', 'read'),
  routes: [
    { path: ROUTES.logistics, breadcrumb: ['Commerce', 'Logistics'], render: () => <LogisticsPage /> },
    {
      path: ROUTES.logisticsOrder,
      breadcrumb: ['Logistics', 'Delivery order'],
      render: (q) => <LogisticsOrderPage id={q.get('id') ?? ''} />,
    },
    {
      path: ROUTES.logisticsTour,
      breadcrumb: ['Logistics', 'Tour'],
      render: (q) => <LogisticsTourPage id={q.get('id') ?? ''} />,
    },
  ],
  nav: {
    section: 'Commerce',
    item: {
      kind: 'leaf',
      key: ROUTES.logistics,
      label: 'Logistics',
      icon: <TruckIcon />,
      href: ROUTES.logistics,
    },
  },
};

/** Sales / ecommerce-capacity (R-075/D-078) — Commerce section, RBAC `ecommerce` module. */
export const salesFeature: FeatureManifest = {
  key: 'sales',
  enabled: ({ mode, can }) => mode === 'cloud' && can('ecommerce', 'read'),
  routes: [{ path: ROUTES.sales, breadcrumb: ['Commerce', 'Sales'], render: () => <SalesPage /> }],
  nav: {
    section: 'Commerce',
    item: { kind: 'leaf', key: ROUTES.sales, label: 'Sales', icon: <BarChartIcon />, href: ROUTES.sales },
  },
};

/** User administration — cloud-only, gated on `users` (D-039/D-043).
 *
 * ⚠ R-349 — was `userType === 'admin'`. Admins still pass (`canWith` bypasses on
 * the type), so nothing an administrator sees changed; what changed is that an
 * `hq` member holding the module by ROLE is no longer refused by a check that
 * never consulted the matrix. The backend moved in the same change — this gate
 * and `gateway-cloud`'s now ask the same question, which is the only way the nav
 * can be trusted not to show a screen that 403s. */

export const usersFeature: FeatureManifest = {
  key: 'users',
  enabled: ({ mode, can }) => mode === 'cloud' && can('users', 'read'),
  routes: [
    {
      path: ROUTES.usersManagement,
      breadcrumb: ['Users', 'User Management'],
      render: () => <UserManagementPage />,
    },
  ],
  nav: {
    section: 'Administration',
    item: {
      kind: 'group',
      key: 'admin-users',
      label: 'Users',
      icon: <UsersIcon />,
      children: [{ key: ROUTES.usersManagement, label: 'User Management', href: ROUTES.usersManagement }],
    },
  },
};

/** Access & Roles console — cloud-only, gated on `access` (R-058 B, D-039). */
// Shared "Configuration" nav group. Both the cloud-only Commons feature and the
// both-tier Locations feature declare a group with THIS key; buildRegistry merges
// same-key groups, so each tier shows only the children whose feature is enabled
// (site → Locations only; cloud → Commons + Locations). (User-directed 2026-07-22.)
const CONFIG_GROUP_KEY = 'itsm/configuration';

// Location hierarchy (R-121) — the coded location SSOT (W2/D-107). Site-authored:
// operational on the LOCAL stack; cloud mode reads through the /sites/{id} proxy.
export const locationsFeature: FeatureManifest = {
  key: 'locations',
  enabled: ({ can }) => can('assets', 'read'),
  routes: [{ path: ROUTES.locations, breadcrumb: ['ITSM', 'Locations'], render: () => <LocationsPage /> }],
  nav: {
    section: 'ITSM',
    item: {
      kind: 'group',
      key: CONFIG_GROUP_KEY,
      label: 'Configuration',
      icon: <WrenchIcon />,
      children: [{ key: ROUTES.locations, label: 'Locations', href: ROUTES.locations }],
    },
  },
};

/** Inviting somebody — split out of `usersFeature` by R-349.
 *
 * ⚠ A screen whose ONLY purpose is a write does not belong behind a read gate.
 * `users: read` earns the directory; it does not earn the power to mint accounts,
 * and a page that can do nothing but 403 is worse than one that is absent.
 *
 * It contributes its leaf to the SAME `admin-users` nav group, which `buildRegistry`
 * merges by `key` — so a reader sees Users → User Management, and an administrator
 * sees Registration above it, from one group. */
export const usersRegistrationFeature: FeatureManifest = {
  key: 'users-registration',
  enabled: ({ mode, can }) => mode === 'cloud' && can('users', 'admin'),
  routes: [
    {
      path: ROUTES.usersRegistration,
      breadcrumb: ['Users', 'Registration'],
      render: () => <RegistrationPage />,
    },
  ],
  nav: {
    section: 'Administration',
    item: {
      kind: 'group',
      key: 'admin-users',
      label: 'Users',
      icon: <UsersIcon />,
      children: [{ key: ROUTES.usersRegistration, label: 'Registration', href: ROUTES.usersRegistration }],
    },
  },
};

export const accessFeature: FeatureManifest = {
  key: 'access',
  enabled: ({ mode, can }) => mode === 'cloud' && can('access', 'read'),
  routes: [
    {
      path: ROUTES.access,
      breadcrumb: ['Administration', 'Access & Roles'],
      render: () => <AccessRolesPage />,
    },
  ],
  nav: {
    section: 'Administration',
    item: {
      kind: 'leaf',
      key: ROUTES.access,
      label: 'Access & Roles',
      icon: <ShieldIcon />,
      href: ROUTES.access,
    },
  },
};

/** ITSM commons admin (R-131) — cloud-owned masters synced down to every
 * site (D-105). Cloud tier, `maintenance` write to edit. */
export const itsmCommonsFeature: FeatureManifest = {
  key: 'itsm-commons',
  enabled: ({ mode, can }) => mode === 'cloud' && can('maintenance', 'read'),
  // ITSM domain configuration: one page whose in-page tabs (matrix/SLA/routing/
  // groups/on-call/templates/KB/benchmarks/taxonomy/tokens) are its own
  // sub-navigation — a single leaf nav entry, not 10 left-rail children.
  routes: [
    {
      path: ROUTES.itsmCommons,
      breadcrumb: ['ITSM', 'Commons'],
      render: (q) => <ItsmCommonsPage initialTab={q.get('tab')} />,
    },
  ],
  nav: {
    // ITSM domain configuration — "Commons" leaf under the shared "Configuration"
    // group (merged with Locations by CONFIG_GROUP_KEY). Its in-page tabs
    // (matrix/SLA/templates/KB/benchmarks/taxonomy/tokens) are its own
    // sub-navigation. Cloud-tier only. (User-directed 2026-07-22.)
    section: 'ITSM',
    item: {
      kind: 'group',
      key: CONFIG_GROUP_KEY,
      label: 'Configuration',
      icon: <WrenchIcon />,
      children: [{ key: ROUTES.itsmCommons, label: 'Commons', href: ROUTES.itsmCommons }],
    },
  },
};

/** Sites console — cloud-only, gated on `settings` (R-150). Create/configure fleet sites + mint the
 * one-time enrollment token a local server uses to self-register (zero-touch onboarding). */
export const sitesFeature: FeatureManifest = {
  key: 'sites',
  enabled: ({ mode, can }) => mode === 'cloud' && can('settings', 'read'),
  // R-152: the unified fleet page — servers that phoned home (claim → site) + the sites they run,
  // all in one list. (Superseded the separate Servers page + Onboarding group.)
  routes: [{ path: ROUTES.sites, breadcrumb: ['Administration', 'Sites'], render: () => <SitesPage /> }],
  nav: {
    section: 'Administration',
    item: { kind: 'leaf', key: ROUTES.sites, label: 'Sites', icon: <CloudIcon />, href: ROUTES.sites },
  },
};

/** Cultivation console (W3/R-157) — crop taxonomy, versioned crop profiles with
 * their 5-band thresholds, configurable grow capacity, and the sign-off surface
 * for every adjudicated source-document conflict. Cloud-only, gated on
 * **`farming: write`** — not `read` — because this is an AUTHORING console: the
 * backend surface behind it is 13 write endpoints and 2 approve against 6 reads,
 * and every screen is a form. Opening it at `read` would hand a reader 56 controls
 * that answer 403, which the rules forbid; the reader's view of the same data is
 * Production → Plans. The two sign-off actions stay separately gated at
 * `farming: approve` inside the page. */
export const cultivationFeature: FeatureManifest = {
  key: 'cultivation',
  enabled: ({ mode, can }) => mode === 'cloud' && can('farming', 'write'),
  // R-204 made this a nav GROUP holding everything about a crop, one leaf per
  // screen — but it spent three of them on ONE hierarchy, because Crops and
  // Varieties were the Master Data tables reused via `only` rather than ported.
  //
  // R-210 folds those three into the Catalogue tree: `crop_categories → crops →
  // crop_varieties` is a single parent-child chain, so browsing it as three flat
  // tables made an operator hold the join in their head. Ordering was then
  // identity+agronomy (Catalogue, Profiles, Capacity) → governance (Conflict
  // flags, Import); Capacity has since left for Production Planning (R-321) and
  // the leaves themselves became tabs (R-330 — see the nav item below). The
  // `cloud && admin` gate has never changed.
  routes: [
    {
      path: ROUTES.cultivationStages,
      breadcrumb: ['Cultivation', 'Stages'],
      render: () => <CultivationPage tab="stages" />,
    },
    {
      path: ROUTES.cultivationCatalogue,
      breadcrumb: ['Cultivation', 'Catalogue'],
      render: () => <CultivationPage tab="catalogue" />,
    },
    {
      path: ROUTES.cultivationProfiles,
      breadcrumb: ['Cultivation', 'Profiles'],
      render: () => <CultivationPage tab="profiles" />,
    },
    // The conflict registry. R-222 took this OUT of the nav and left the route:
    // the sign-off decision now happens on the flagged BAND inside the profile,
    // where the operator is already looking at the number they are accepting.
    // Evidence it belonged there: as a nav page, 1 of 14 flags requiring
    // sign-off had ever been signed.
    //
    // The route stays because the LIST still has a job — a horticulturist doing
    // a go-live pass wants every adjudication and its signature in one place,
    // and any bookmark or audit link must keep resolving. It is a report now,
    // not a step in anyone's workflow.
    {
      path: ROUTES.cultivationFlags,
      breadcrumb: ['Cultivation', 'Conflict flags'],
      render: () => <CultivationPage tab="flags" />,
    },
    {
      path: ROUTES.cultivationImport,
      breadcrumb: ['Cultivation', 'Import'],
      render: () => <CultivationPage tab="import" />,
    },
  ],
  nav: {
    // FARM, not Administration (user, 2026-08-05). Cultivation is what a grower
    // works in — it defines what each crop wants and, since R-207, what the room's
    // equipment actually does about it. It sat under Administration because it is
    // cloud-owned master data, but ownership is not the same question as where an
    // operator looks for it: Production, Room Climate and the Grow Scheduler are
    // all Farm, and this is the data those three read. The `cloud && admin` gate
    // is unchanged — only the placement moves.
    section: 'Farm',
    // R-330: ONE leaf, and the four screens became the page's own tab strip.
    // They were never four pages — every route above renders the SAME
    // `CultivationPage` with a different `tab`, so the nav group was doing a tab
    // bar's job from the sidebar, where it cost an operator a chevron and a
    // second click to move between four views of one thing.
    //
    // ⚠ `key` is the BASE path, `href` is the landing tab. The key is what the
    // sidebar matches on, and `matches()` (nav-tree.tsx) accepts a prefix — so
    // keying on the base keeps this leaf highlighted on all five tab paths,
    // including `flags`, which has no strip entry. Keying it on the Stages path
    // would light the leaf up on Stages only and leave the sidebar looking empty
    // on the other three.
    //
    // Landing on STAGES, for R-251's reason: a crop cannot be created before the
    // farm's stage vocabulary exists, so it is the tab that has to make sense
    // first.
    //
    // If a second Cultivation surface ever lands (one that is NOT a view of a
    // crop's definition), promote this back to `kind: 'group'` with this leaf as
    // its first child — nothing else in the app distinguishes the two.
    item: {
      kind: 'leaf',
      key: ROUTES.cultivation,
      label: 'Cultivation',
      icon: <LeafIcon />,
      href: ROUTES.cultivationStages,
    },
  },
};

/** Crop alerts — its OWN Farm leaf, not a child of Production (console R-357).
 *
 * It used to sit inside the Production group, one click deep, which is the wrong
 * depth for the one screen that says something is going wrong RIGHT NOW: the
 * other four Production leaves are places you plan from, and this is the place
 * you react from. It leads the Farm section for the same reason — an operator
 * opening the farm wants to know what is complaining before they browse.
 *
 * A separate manifest, not a second `nav` on `productionPlanningFeature`, because
 * `FeatureManifest.nav` is one entry per feature and section order follows the
 * FEATURES array — so being its own manifest is what makes "first in Farm"
 * expressible at all. Gating is identical to Production's, so the two never
 * disagree about who may see the rows. */
export const planningAlertsFeature: FeatureManifest = {
  key: 'planning-alerts',
  enabled: ({ mode, can }) => mode === 'cloud' && can('farming', 'read'),
  routes: [
    {
      path: ROUTES.planningAlerts,
      breadcrumb: ['Farm', 'Alerts'],
      // `?plan=` / `?room=` preselect the filters, so an email deep link and the
      // plan record's "see its alerts" land on the rows they promised.
      render: (q) => <PlanningAlertsPage planId={q.get('plan') ?? ''} roomCode={q.get('room') ?? ''} />,
    },
  ],
  nav: {
    section: 'Farm',
    item: {
      kind: 'leaf',
      key: ROUTES.planningAlerts,
      label: 'Alerts',
      // Deliberately NOT AlertTriangleIcon — that is SOC's mark in Monitor, and
      // two different screens wearing one icon in one sidebar is a misread.
      icon: <AlertCircleIcon />,
      href: ROUTES.planningAlerts,
    },
  },
};

/** Production planning — Farm section, RBAC `farming` (R-320).
 *
 * Cloud-tier, for the same reason the scheduler is: a rack is committed
 * fleet-wide before a site ever sees the crop, and crop yield is master data.
 *
 * Reads need `read`; configuring capacity, drafting a plan and working a task
 * need `write`; **scheduling, completing, cancelling and failing a plan need
 * `approve`** — scheduling is the commit point where a placement stops being a
 * sketch and becomes shelving committed plus somebody's day. Each page enforces
 * the last two itself, so the nav stays visible to anyone who may look.
 *
 * The record is a locked sub-page under Plans, so that leaf stays highlighted
 * while a plan is open — the same pattern the incident and change records use. */
export const productionPlanningFeature: FeatureManifest = {
  key: 'production-planning',
  enabled: ({ mode, can }) => mode === 'cloud' && can('farming', 'read'),
  routes: [
    {
      path: ROUTES.planningCapacity,
      breadcrumb: ['Production', 'Capacity'],
      render: () => <CapacityPage />,
    },
    {
      path: ROUTES.planningPlans,
      breadcrumb: ['Production', 'Plans'],
      // The query carries a demand when the Demands screen sent us here, so the
      // wizard opens on that crop and quantity instead of empty.
      render: (q) => (
        <PlansPage
          onOpen={(id) => navigate(href(ROUTES.planningPlanRecord, { id }))}
          seed={{
            cropId: q.get('crop') ?? undefined,
            varietyId: q.get('variety') ?? undefined,
            kg: q.get('kg') ?? undefined,
            start: q.get('start') ?? undefined,
          }}
        />
      ),
    },
    {
      path: ROUTES.planningPlanRecord,
      breadcrumb: ['Production', 'Plan'],
      render: (q) => <PlanRecordPage id={q.get('id') ?? ''} onBack={() => navigate(ROUTES.planningPlans)} />,
    },
    {
      path: ROUTES.planningTimeline,
      breadcrumb: ['Production', 'Timeline'],
      render: () => (
        <PlanningTimelinePage onOpen={(id) => navigate(href(ROUTES.planningPlanRecord, { id }))} />
      ),
    },
    {
      path: ROUTES.planningTasks,
      breadcrumb: ['Production', 'Tasks'],
      render: () => <PlanningTasksPage />,
    },
  ],
  nav: {
    section: 'Farm',
    item: {
      kind: 'group',
      key: 'production-planning',
      label: 'Production',
      icon: <RackIcon />,
      children: [
        { key: ROUTES.planningPlans, label: 'Plans', href: ROUTES.planningPlans },
        { key: ROUTES.planningTimeline, label: 'Timeline', href: ROUTES.planningTimeline },
        { key: ROUTES.planningTasks, label: 'Tasks', href: ROUTES.planningTasks },
        { key: ROUTES.planningCapacity, label: 'Capacity', href: ROUTES.planningCapacity },
      ],
    },
  },
};

/** Master data console — cloud-only, gated on `farming` (R-062/D-063). */
export const masterDataFeature: FeatureManifest = {
  key: 'master-data',
  enabled: ({ mode, can }) => mode === 'cloud' && can('farming', 'read'),
  routes: [
    {
      path: ROUTES.masterData,
      breadcrumb: ['Administration', 'Master Data'],
      render: () => <MasterDataPage />,
    },
  ],
  nav: {
    section: 'Administration',
    item: {
      kind: 'leaf',
      key: ROUTES.masterData,
      label: 'Master Data',
      icon: <LayersIcon />,
      href: ROUTES.masterData,
    },
  },
};

/** Device catalog console — cloud-only, gated on `devices` (R-097/D-090).
 * The same module the SITE tier already gates its catalog route on, so one grant
 * means the same thing at both tiers. */
export const deviceCatalogFeature: FeatureManifest = {
  key: 'device-catalog',
  enabled: ({ mode, can }) => mode === 'cloud' && can('devices', 'read'),
  routes: [
    {
      path: ROUTES.deviceCatalog,
      breadcrumb: ['Administration', 'Device Catalog'],
      render: () => <DeviceCatalogPage />,
    },
  ],
  nav: {
    section: 'Administration',
    item: {
      kind: 'leaf',
      key: ROUTES.deviceCatalog,
      label: 'Device Catalog',
      icon: <ChipIcon />,
      href: ROUTES.deviceCatalog,
    },
  },
};

/** Audit trail — cloud-only, gated on `soc` (R-059 D5). */
/** Personal notifications inbox (R-112) — reached from the top-bar bell's
 *  "See all" AND from its own HQ nav leaf. Own-data, any cloud user.
 *
 *  Under HQ, not Account: the bell is a transient peek at the last 30, while the
 *  inbox is the standing cross-site record of what the fleet asked of you — the
 *  same "everything, every site" altitude as Fleet Overview / Fleet Inventory.
 *  Cloud-only because the API is gateway-cloud's (`/users/v1/notifications`);
 *  HQ is a cloud section too, so the two gates agree. */
export const notificationsFeature: FeatureManifest = {
  key: 'notifications',
  enabled: ({ mode }) => mode === 'cloud',
  routes: [
    {
      path: ROUTES.notifications,
      breadcrumb: ['HQ', 'Notifications'],
      render: () => <NotificationsPage />,
    },
  ],
  nav: {
    section: 'HQ',
    item: {
      kind: 'leaf',
      key: ROUTES.notifications,
      label: 'Notifications',
      icon: <BellIcon />,
      href: ROUTES.notifications,
    },
  },
};

export const auditFeature: FeatureManifest = {
  key: 'audit',
  enabled: ({ mode, can }) => mode === 'cloud' && can('soc', 'read'),
  routes: [
    { path: ROUTES.audit, breadcrumb: ['Administration', 'Audit Log'], render: () => <AuditLogPage /> },
  ],
  nav: {
    section: 'Administration',
    item: {
      kind: 'leaf',
      key: ROUTES.audit,
      label: 'Audit Log',
      icon: <ScrollTextIcon />,
      href: ROUTES.audit,
    },
  },
};

export const historyFeature: FeatureManifest = {
  key: 'history',
  // Both tiers; gated by the `history` module (R-088).
  enabled: ({ can }) => can('history', 'read'),
  routes: [
    {
      path: ROUTES.historyMicrocontrollers,
      breadcrumb: ['History', 'Microcontrollers'],
      render: () => <McHistoryPage />,
    },
    {
      path: ROUTES.historySignals,
      breadcrumb: ['History', 'Device History'],
      render: () => <DeviceHistoryPage />,
    },
  ],
  nav: {
    section: 'History',
    item: {
      kind: 'group',
      key: 'hist-iot',
      label: 'IoT Devices',
      icon: <ChipIcon />,
      children: [
        {
          key: ROUTES.historyMicrocontrollers,
          label: 'Microcontrollers',
          href: ROUTES.historyMicrocontrollers,
        },
        {
          key: ROUTES.historySignals,
          label: 'Device History',
          href: ROUTES.historySignals,
        },
      ],
    },
  },
};

/** Sentinel Mobile Agent (R-147 W6) — the technician's `/m/*` phone surface.
 * Route-only, NO sidebar entry (precedent: notificationsFeature) — the Router
 * renders these inside the MobileShellLayout (bottom nav) on the /m prefix.
 * Local tier + maintenance write only: it is a field-execution surface. */
export const technicianMobileFeature: FeatureManifest = {
  key: 'technician-mobile',
  enabled: ({ mode, can }) => mode === 'local' && can('maintenance', 'write'),
  routes: [
    { path: ROUTES.mHome, breadcrumb: ['Technician', 'Home'], render: () => <MobileHomePage /> },
    {
      // Task detail = the same path + `?kind=&id=` (no extra route).
      path: ROUTES.mWork,
      breadcrumb: ['Technician', 'My Work'],
      render: (q) =>
        q.get('id') ? (
          <TaskDetailPage kind={q.get('kind') ?? 'incident'} id={q.get('id') ?? ''} />
        ) : (
          <MyWorkPage />
        ),
    },
    {
      path: ROUTES.mScan,
      breadcrumb: ['Technician', 'Scan'],
      render: (q) => <MobileScanPage tag={q.get('tag')} />,
    },
    { path: ROUTES.mProfile, breadcrumb: ['Technician', 'Profile'], render: () => <MobileProfilePage /> },
  ],
};

/** Account settings — routed page, no sidebar section (shells link it from the sidebar foot). */
export const settingsFeature: FeatureManifest = {
  key: 'settings',
  routes: [{ path: ROUTES.settings, breadcrumb: ['Account', 'Settings'], render: () => <SettingsPage /> }],
};

/** Public auth flows (no login required) — rendered outside the shell. */
export const authFlowsFeature: FeatureManifest = {
  key: 'auth-flows',
  routes: [
    {
      path: ROUTES.completeRegistration,
      breadcrumb: ['', 'Complete Registration'],
      public: true,
      render: (q) => <CompleteRegistrationPage token={q.get('token') ?? ''} />,
    },
    {
      path: ROUTES.recover,
      breadcrumb: ['', 'Password Recovery'],
      public: true,
      render: (q) => <RecoverPage email={q.get('email') ?? ''} otp={q.get('otp') ?? ''} />,
    },
    {
      path: ROUTES.mfaRecover,
      breadcrumb: ['', 'Two-factor Recovery'],
      public: true,
      render: (q) => <MfaRecoverPage token={q.get('token') ?? ''} />,
    },
  ],
};
